import React from 'react';
import { Badge, Card, Row, Col } from 'react-bootstrap';
import { FaUser, FaEnvelope, FaPhone } from 'react-icons/fa';

/**
 * ContactDisplay Component
 * 
 * Displays contact information in a clean, organized format
 * Used for showing contacts in customer and site details
 */
const ContactDisplay = ({ contacts = [], title = "Contacts", compact = false }) => {
  if (!contacts || contacts.length === 0) {
    return (
      <div className={compact ? "text-muted small" : "text-muted"}>
        No contacts available
      </div>
    );
  }

  if (compact) {
    // Compact view for table cells
    return (
      <div className="contacts-compact">
        {contacts.map((contact, index) => (
          <div key={contact.id || index} className="mb-1 small">
            <div className="fw-bold">
              <FaUser className="me-1" size="0.8em" />
              {contact.name}
            </div>
            {contact.email && (
              <div className="text-muted">
                <FaEnvelope className="me-1" size="0.7em" />
                {contact.email}
              </div>
            )}
            {contact.phone_numbers && contact.phone_numbers.length > 0 && (
              <div className="text-muted">
                <FaPhone className="me-1" size="0.7em" />
                {contact.phone_numbers[0].number}
                {contact.phone_numbers.length > 1 && (
                  <span className="text-muted"> (+{contact.phone_numbers.length - 1} more)</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Full view for expanded details
  return (
    <div className="contacts-display">
      <h6 className="contacts-display-title mb-3">
        <FaUser className="me-2" />
        {title} ({contacts.length})
      </h6>
      
      <Row>
        {contacts.map((contact, index) => (
          <Col key={contact.id || index} md={6} lg={4} className="mb-3">
            <Card className="h-100 contact-card">
              <Card.Body className="p-3">
                <div className="d-flex align-items-start mb-2">
                  <FaUser className="text-primary me-2 mt-1" />
                  <div className="flex-grow-1">
                    <h6 className="mb-1 card-title">{contact.name}</h6>
                    {contact.email && (
                      <div className="mb-2">
                        <FaEnvelope className="text-secondary me-2" size="0.9em" />
                        <a 
                          href={`mailto:${contact.email}`} 
                          className="text-decoration-none small"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {contact.email}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                
                {contact.phone_numbers && contact.phone_numbers.length > 0 && (
                  <div className="phone-numbers">
                    <div className="phone-numbers-label mb-1">
                      <FaPhone className="text-secondary me-2" size="0.9em" />
                      <span className="phone-numbers-text">Phone Numbers:</span>
                    </div>
                    {contact.phone_numbers.map((phone, phoneIndex) => (
                      <div key={phoneIndex} className="mb-1 ms-3">
                        <Badge
                          bg="outline-secondary"
                          text="dark"
                          className="me-2 small"
                        >
                          {phone.type}
                        </Badge>
                        <a
                          href={`tel:${phone.number}`}
                          className="text-decoration-none small"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {phone.number}
                        </a>
                      </div>
                    ))}
                  </div>
                )}

                {(!contact.phone_numbers || contact.phone_numbers.length === 0) && !contact.email && (
                  <div className="text-muted small">
                    No contact information available
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default ContactDisplay;